/**
 * PII matrix + simulate-scrubbed (PR Q3 · §Q3.2).
 *
 * Atlas's compliance lens. Three default regimes (GDPR / CCPA / HIPAA);
 * future regimes plug in by extending `REGIME_RULES`. PII detection is
 * a union of explicit `pii:*` field tags and name-pattern heuristics —
 * surfacing both lets compliance leads catch fields the schema author
 * forgot to tag.
 *
 * `buildPiiMatrix` returns the full entity × field × regime matrix
 * plus a per-regime summary for the chip strip.
 *
 * `simulateScrubbed` returns a shallow-redacted copy of a sample
 * payload — the "hand to legal as evidence" mode.
 */
import type { AtlasManifest } from '@atlas-manifest/types';

export type RegimeId = 'gdpr' | 'ccpa' | 'hipaa';

export interface PiiMatrixCell {
  entityId: string;
  field: string;
  regime: RegimeId;
  applies: boolean;
  stripOnExport: boolean;
}

export interface PiiMatrixSummary {
  /** Distinct entities with at least one applying field. */
  entityCount: number;
  /** Total applying field cells. */
  fieldCount: number;
}

export interface PiiMatrix {
  cells: PiiMatrixCell[];
  summary: Record<RegimeId, PiiMatrixSummary>;
}

export interface RegimeRule {
  id: RegimeId;
  label: string;
  /**
   * Entity-tag prefixes that opt the entity into the regime. e.g. for
   * GDPR: any tag starting with `pii:`. The tag's suffix (after `pii:`)
   * is matched against the field name as a fast-path opt-in.
   */
  entityTagMatchers: ((tag: string) => boolean)[];
  /** Name-pattern heuristics — applied as a fallback when no tag matches. */
  namePatterns: RegExp[];
}

const COMMON_PII = [/^email/i, /^phone/i, /^address/i, /name$/i, /^postal/i, /^street/i, /^city/i];

export const REGIME_RULES: readonly RegimeRule[] = [
  {
    id: 'gdpr',
    label: 'GDPR',
    entityTagMatchers: [(t) => t.startsWith('pii:')],
    namePatterns: COMMON_PII,
  },
  {
    id: 'ccpa',
    label: 'CCPA',
    entityTagMatchers: [(t) => t.startsWith('pii:')],
    namePatterns: COMMON_PII,
  },
  {
    id: 'hipaa',
    label: 'HIPAA',
    entityTagMatchers: [
      (t) => t === 'pii:ssn' || t === 'pii:medical' || t === 'pii:dob',
    ],
    namePatterns: [/dateOfBirth/i, /ssn/i, /medical/i, /diagnosis/i, /healthSavings/i],
  },
] as const;

export const REGIMES: readonly RegimeId[] = REGIME_RULES.map((r) => r.id);

/**
 * Tag-driven match: when an entity tag is `pii:<suffix>`, the suffix
 * has to match the field name (case-insensitive substring) — e.g.
 * `pii:email` on the entity opts the `email` field in for GDPR / CCPA.
 *
 * R1.M3 — tags are trimmed and lowercased before matching so manifest
 * authors writing `PII:Email`, ` pii:email `, etc. don't silently miss
 * the regime. All matchers and suffixes operate on the normalized form.
 */
function tagAppliesToField(rule: RegimeRule, tag: string, fieldName: string): boolean {
  const normalized = tag.trim().toLowerCase();
  if (!rule.entityTagMatchers.some((m) => m(normalized))) return false;
  const suffix = normalized.split(':')[1] ?? '';
  if (!suffix) return false;
  return fieldName.toLowerCase().includes(suffix);
}

function appliesToField(
  rule: RegimeRule,
  field: { name: string },
  entityTags: string[],
): boolean {
  // 1. Explicit entity tag matches → strong opt-in.
  if (entityTags.some((t) => tagAppliesToField(rule, t, field.name))) return true;
  // 2. Fallback: heuristic name pattern.
  return rule.namePatterns.some((p) => p.test(field.name));
}

function stripOnExportFor(applies: boolean): boolean {
  // For our default regimes, every applying PII field strips on export.
  // HIPAA conditional cases (e.g. "retain N years") can be expressed
  // later via per-rule overrides on the cell.
  return applies;
}

export function buildPiiMatrix(
  manifest: AtlasManifest,
  regimes: readonly RegimeId[] = REGIMES,
): PiiMatrix {
  const rules = REGIME_RULES.filter((r) => regimes.includes(r.id));
  const cells: PiiMatrixCell[] = [];
  for (const entity of manifest.entities) {
    for (const field of entity.fields) {
      for (const rule of rules) {
        const applies = appliesToField(rule, field, entity.tags);
        cells.push({
          entityId: entity.id,
          field: field.name,
          regime: rule.id,
          applies,
          stripOnExport: stripOnExportFor(applies),
        });
      }
    }
  }
  const summary = {} as Record<RegimeId, PiiMatrixSummary>;
  for (const rule of REGIME_RULES) {
    const applyingCells = cells.filter((c) => c.regime === rule.id && c.applies);
    const entitySet = new Set(applyingCells.map((c) => c.entityId));
    summary[rule.id] = {
      entityCount: entitySet.size,
      fieldCount: applyingCells.length,
    };
  }
  return { cells, summary };
}

export interface ScrubResult {
  scrubbed: Record<string, unknown>;
  redactedKeys: string[];
}

const REDACTED = '[redacted]';

export function simulateScrubbed(
  sample: Record<string, unknown>,
  entityId: string,
  manifest: AtlasManifest,
  regime: RegimeId,
): ScrubResult {
  const entity = manifest.entities.find((e) => e.id === entityId);
  if (!entity) return { scrubbed: { ...sample }, redactedKeys: [] };
  const rule = REGIME_RULES.find((r) => r.id === regime);
  if (!rule) return { scrubbed: { ...sample }, redactedKeys: [] };

  const fieldsByName = new Map(entity.fields.map((f) => [f.name, f]));
  const scrubbed: Record<string, unknown> = {};
  const redacted: string[] = [];
  for (const [k, v] of Object.entries(sample)) {
    const f = fieldsByName.get(k);
    if (f && appliesToField(rule, f, entity.tags)) {
      scrubbed[k] = REDACTED;
      redacted.push(k);
    } else {
      scrubbed[k] = v;
    }
  }
  return { scrubbed, redactedKeys: redacted };
}
